import Close16 from '@carbon/icons-react/es/close/16';
import Cut16 from '@carbon/icons-react/es/cut/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Space, Tooltip } from 'antd';
import { Dictionary } from 'lodash';
import moment from 'moment';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { Team, User } from 'api/models';
import { AllocationMedium, ResourceType } from 'api/resource-management/types';
import { TimeoffResponse } from 'api/timeoff/types';
import { Alert } from 'components/atoms/alert';
import { Button } from 'components/atoms/button';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent as DrawerContent,
} from 'components/atoms/drawer';
import { Form } from 'components/atoms/form';
import { InputNumber } from 'components/atoms/input';
import { NonNullInputDuration } from 'components/atoms/input-duration';
import { Loading } from 'components/atoms/loading';
import { Radio } from 'components/atoms/radio';
import { formatTime } from 'components/organisms/timesheets/common/format-time';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { Caption01 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { queueMacrotask } from 'utils/macrotask';
import { getTimeOffArray } from 'utils/time-off-utils';
import { ProjectsWithMembers } from '../types';
import { getDateAfter } from '../utils/dates';
import { TeamDetail } from './team-detail';
import { UserDetail } from './user-detail';

const CaptionStyles = css`
  ${Caption01};
  color: var(--gray-70);
`;

const AllocationHelpText = styled.div`
  ${CaptionStyles};
`;

const AllocationHelpSpan = styled.span`
  ${CaptionStyles};
`;
const StyledButton = styled(Button)``;
const Content = styled(DrawerContent)`
  width: 360px;
`;

const StyledButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-05);
  ${Button} {
    flex: 1;
  }
  ${StyledButton} {
    flex: 0;
  }
`;

const DrawerContentContent = styled(DrawerContent.Content)`
  display: flex;
  flex-direction: column;
  > :first-child {
    flex-grow: 1;
  }
`;

const AllocationTypeDescription = styled.span`
  display: inline-flex;
  flex-direction: column;
`;

const StyledFormItem = styled(Form.Item)`
  padding-top: var(--layout-01);
`;

const DEFAULT_ALLOCATION_PER_DAY = 60; // in minutes

interface AllocationOptionProps {
  type: AllocationMedium;
  title: ReactNode;
  subtitle?: ReactNode;
}

const AllocationTypeOption: FC<AllocationOptionProps> = ({
  type,
  title,
  subtitle,
}) => (
  <Radio value={type}>
    <AllocationTypeDescription>
      {title}
      <AllocationHelpSpan>{subtitle}</AllocationHelpSpan>
    </AllocationTypeDescription>
  </Radio>
);

export interface BaseAllocationParams {
  eventId?: number; // will get eventId if in edit mode
  projectId: number;
  minutes: number;
  workingDays: number;
  billable: boolean;
  projectPhase?: {
    id: number;
    name: string;
  };
  medium: AllocationMedium;
  startDate: Date | string;
  endDate: Date | string;
}

export interface AllocationParamsForUsers extends BaseAllocationParams {
  type: ResourceType.User;
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
}

export interface AllocationParamsForTeams extends BaseAllocationParams {
  type: ResourceType.Team;
  teamId: number;
  roleId: number;
}

export type AllocationModalParams =
  | AllocationParamsForUsers
  | AllocationParamsForTeams;

interface Props {
  projectLookup: Dictionary<ProjectsWithMembers>;
  allocationDetail: AllocationModalParams;
  hideAllocationMediumControls?: boolean;
  softAllocationsEnabled: boolean;
  onClose: () => void;
  onDone: (detail: AllocationModalParams) => Promise<void>;
  onDelete: () => Promise<void>;
  onEnableSplitMode: (eventId?: number) => void;
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  workingDaysConfig: WorkingDaysConfiguration;
  isHoursPerWeek?: boolean;
}

export const ManageAllocationDrawer: FC<Props> = ({
  projectLookup,
  allocationDetail,
  softAllocationsEnabled,
  onClose,
  onDone,
  onDelete,
  onEnableSplitMode,
  userLookup,
  teamLookup,
  hideAllocationMediumControls,
  workingDaysConfig,
  isHoursPerWeek,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [totalAllocation, setTotalAllocation] = useState(0);
  const [allocationEndDate, setAllocationEndDate] = useState(
    moment(allocationDetail.endDate),
  );
  const [allocationMinutes, setAllocationMinutes] =
    useState<number | undefined>(undefined);
  const [timeOff, setTimeOff] = useState<TimeoffResponse[]>();

  const allocationHoursInputRef = React.useRef<any>(null);
  const [form] = Form.useForm();

  const inputPlaceholder = useTranslation(
    isHoursPerWeek
      ? 'resource_management.allocations.time_per_week.placeholder'
      : 'resource_management.allocations.time_per_day.placeholder',
  );

  const user = useMemo(
    () =>
      allocationDetail.type === ResourceType.User
        ? userLookup[allocationDetail.userId]
        : undefined,
    [allocationDetail, userLookup],
  );
  const team = useMemo(
    () =>
      allocationDetail.type === ResourceType.Team
        ? teamLookup[allocationDetail.teamId]
        : undefined,
    [allocationDetail, teamLookup],
  );
  const referenceTeamName = useMemo(
    () =>
      allocationDetail.type === ResourceType.User &&
      allocationDetail.referenceTeamId
        ? projectLookup[allocationDetail.projectId].projectMembers.find(
            ({ user: { userId }, referenceTeam }) =>
              referenceTeam != null &&
              userId === allocationDetail.userId &&
              referenceTeam.teamId === allocationDetail.referenceTeamId,
          )?.referenceTeam?.teamName
        : undefined,
    [allocationDetail, projectLookup],
  );

  const allowSubmit = useMemo(
    () => allocationMinutes != null && allocationMinutes > 0,
    [allocationMinutes],
  );

  const showSplitMode = useMemo(
    () =>
      moment(allocationDetail.endDate)
        .startOf('day')
        .diff(moment(allocationDetail.startDate).startOf('day'), 'days') >= 1,
    [allocationDetail.endDate, allocationDetail.startDate],
  );

  const onSave = useCallback(() => {
    if (allocationMinutes != null) {
      setIsSubmitting(true);
      form
        .validateFields()
        .then(() => {
          const formValues = form.getFieldsValue();

          onDone({
            ...(allocationDetail.type === ResourceType.User
              ? {
                  type: ResourceType.User,
                  userId: allocationDetail.userId,
                  roleId: allocationDetail.roleId,
                  referenceTeamId: allocationDetail.referenceTeamId,
                }
              : {
                  teamId: allocationDetail.teamId,
                  roleId: allocationDetail.roleId,
                  type: ResourceType.Team,
                }),
            projectId: allocationDetail.projectId,
            minutes: allocationMinutes,
            workingDays: isHoursPerWeek
              ? formValues.workingDays *
                Object.values(workingDaysConfig).filter(Boolean).length
              : formValues.workingDays,
            billable: allocationDetail.billable ?? true,
            medium:
              formValues.allocationMedium ??
              allocationDetail.medium ??
              AllocationMedium.Hard,
            startDate: allocationDetail.startDate,
            endDate: allocationDetail.endDate,
          })
            .then(() => {
              form.resetFields();
            })
            .finally(() => {
              setIsSubmitting(false);
            });
        })
        .catch(() => {});
    }
  }, [
    allocationDetail,
    allocationMinutes,
    form,
    onDone,
    isHoursPerWeek,
    workingDaysConfig,
  ]);

  const onCancel = useCallback(() => {
    onClose();
    form.resetFields();
  }, [form, onClose]);

  const checkIfTimeOffPresent = useCallback(
    (days: number) => {
      if (allocationDetail.type === ResourceType.User) {
        const endDate = moment(allocationDetail.startDate).add(days, 'days');

        const timeOffTemp = getTimeOffArray(
          moment(allocationDetail.startDate),
          endDate,
          userLookup?.[allocationDetail.userId]?.userTimeOffDetails,
        );

        setTimeOff(timeOffTemp);
      }
    },
    [allocationDetail, userLookup],
  );

  useEffect(() => {
    setAllocationEndDate(moment(allocationDetail.endDate));
  }, [allocationDetail.endDate]);

  useEffect(() => {
    form.setFieldsValue({
      workingDays: isHoursPerWeek
        ? allocationDetail.workingDays /
          Object.values(workingDaysConfig).filter(Boolean).length
        : allocationDetail.workingDays,
      allocationMedium: allocationDetail.medium,
    });
    setTotalAllocation(allocationDetail.minutes * allocationDetail.workingDays);
    setAllocationMinutes(allocationDetail.minutes);
    queueMacrotask(() => {
      allocationHoursInputRef.current?.focus();
    });
  }, [allocationDetail, form, isHoursPerWeek, workingDaysConfig]);

  useEffect(() => {
    if (allocationDetail.type === ResourceType.User) {
      const timeOffTemp = getTimeOffArray(
        moment(allocationDetail.startDate),
        moment(allocationDetail.endDate),
        userLookup?.[allocationDetail.userId]?.userTimeOffDetails,
      );

      setTimeOff(timeOffTemp);
    }
  }, [allocationDetail, userLookup]);

  return (
    <CustomLayoutDrawer
      onClose={onCancel}
      maskClosable={false}
      mask
      destroyOnClose
      width={360}
      closeIcon={<Close16 />}
      visible
    >
      <Content>
        <DrawerContent.Title>
          {user != null ? (
            <UserDetail user={user} referenceTeamName={referenceTeamName} />
          ) : team != null ? (
            <TeamDetail team={team} />
          ) : null}
        </DrawerContent.Title>
        <DrawerContentContent>
          <Form
            layout="vertical"
            form={form}
            onValuesChange={(modifiedValues, values: any) => {
              if (modifiedValues.workingDays) {
                setAllocationEndDate(
                  getDateAfter(
                    moment(allocationDetail.startDate),
                    workingDaysConfig,
                    isHoursPerWeek
                      ? modifiedValues.workingDays *
                          Object.values(workingDaysConfig).filter(Boolean)
                            .length
                      : modifiedValues.workingDays,
                  ),
                );
                checkIfTimeOffPresent(modifiedValues.workingDays);
                setTotalAllocation(
                  (allocationMinutes ?? DEFAULT_ALLOCATION_PER_DAY) *
                    values.workingDays,
                );
              }
            }}
          >
            <Form.Item
              label={
                <FormattedMessage
                  id={
                    isHoursPerWeek
                      ? 'resource_management.allocations.time_per_week.label'
                      : 'resource_management.allocations.time_per_day.label'
                  }
                />
              }
              required
            >
              <NonNullInputDuration
                placeholder={inputPlaceholder}
                min={1}
                max={(isHoursPerWeek ? 120 : 24) * DEFAULT_ALLOCATION_PER_DAY}
                value={allocationMinutes}
                onChange={(minutes) => {
                  setAllocationMinutes(minutes);
                  setTotalAllocation(
                    minutes * form.getFieldValue('workingDays'),
                  );
                }}
                onPressEnter={({ currentTarget }) => currentTarget.blur()}
                ref={allocationHoursInputRef}
              />
            </Form.Item>
            <Form.Item
              label={
                moment(allocationDetail.startDate).isSame(
                  allocationEndDate,
                  'day',
                ) && !isHoursPerWeek ? (
                  <FormattedMessage
                    id={
                      isHoursPerWeek
                        ? 'resource_management.allocations.number_of_working_weeks.label'
                        : 'resource_management.allocations.number_of_working_days.label'
                    }
                    values={{
                      startDate: moment(allocationDetail.startDate).format(
                        'DD MMM',
                      ),
                      strong: (chunks) => <strong>{chunks}</strong>,
                      showDates: !hideAllocationMediumControls,
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id={
                      isHoursPerWeek
                        ? 'resource_management.allocations.number_of_working_weeks_range.label'
                        : 'resource_management.allocations.number_of_working_days_range.label'
                    }
                    values={{
                      startDate: moment(allocationDetail.startDate).format(
                        'DD MMM',
                      ),
                      endDate: allocationEndDate.format('DD MMM'),
                      strong: (chunks) => <strong>{chunks}</strong>,
                      showDates: !hideAllocationMediumControls,
                    }}
                  />
                )
              }
              name="workingDays"
              help={
                totalAllocation !== 0 ? (
                  <AllocationHelpText>
                    <FormattedMessage
                      id="resource_management.allocations.total_allocation.label"
                      values={{
                        time: formatTime(totalAllocation),
                        strong: (chunks) => <strong>{chunks}</strong>,
                      }}
                    />
                  </AllocationHelpText>
                ) : undefined
              }
              required
            >
              <InputNumber
                min={1}
                precision={0}
                onBlur={() => {
                  const workingDays = form.getFieldValue('workingDays');

                  if (workingDays == null) {
                    form.setFieldsValue({ workingDays: 1 });
                  }
                }}
                onPressEnter={({ currentTarget }) => currentTarget.blur()}
              />
            </Form.Item>
            {softAllocationsEnabled && !hideAllocationMediumControls && (
              <StyledFormItem
                name="allocationMedium"
                label={
                  <FormattedMessage id="resource_management.allocations.medium.label" />
                }
                required
              >
                <Radio.Group>
                  <Space direction="vertical" size={12}>
                    <AllocationTypeOption
                      type={AllocationMedium.Hard}
                      title={
                        <FormattedMessage id="resource_management.allocations.medium.hard" />
                      }
                      subtitle={
                        <FormattedMessage id="resource_management.allocations.medium.hard.description" />
                      }
                    />
                    <AllocationTypeOption
                      type={AllocationMedium.Soft}
                      title={
                        <FormattedMessage id="resource_management.allocations.medium.soft" />
                      }
                      subtitle={
                        <FormattedMessage id="resource_management.allocations.medium.soft.description" />
                      }
                    />
                  </Space>
                </Radio.Group>
              </StyledFormItem>
            )}
          </Form>
          {allocationDetail.type === 'user' && !hideAllocationMediumControls ? (
            timeOff && timeOff.length > 0 ? (
              <Alert
                showIcon
                type="warning"
                message={
                  timeOff.length === 1 ? (
                    timeOff[0].timeOffStart === timeOff[0].timeOffEnd ? (
                      <FormattedMessage
                        id="timeoff.indicator.timeoff_warning_single_date"
                        values={{
                          strong: (chunks) => <strong>{chunks}</strong>,
                          user: user ? fullName(user) : '',
                          date: moment(timeOff[0].timeOffStart).format(
                            'DD MMM YY',
                          ),
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        id="timeoff.indicator.timeoff_warning_range"
                        values={{
                          strong: (chunks) => <strong>{chunks}</strong>,
                          user: user ? fullName(user) : '',
                          startDate: moment(timeOff[0].timeOffStart).format(
                            'DD MMM YY',
                          ),
                          endDate: moment(timeOff[0].timeOffEnd).format(
                            'DD MMM YY',
                          ),
                        }}
                      />
                    )
                  ) : (
                    <FormattedMessage
                      id="timeoff.indicator.multiple_timeoff_warning_range"
                      values={{
                        strong: (chunks) => <strong>{chunks}</strong>,
                        user: user ? fullName(user) : '',
                        startDate: moment(allocationDetail.startDate).format(
                          'DD MMM YY',
                        ),
                        endDate: allocationEndDate.format('DD MMM YY'),
                      }}
                    />
                  )
                }
              />
            ) : null
          ) : null}
        </DrawerContentContent>
        <DrawerContent.Footer>
          <StyledButtonGroup>
            {allocationDetail.eventId != null ? (
              <>
                <Tooltip
                  title={
                    <FormattedMessage id="resource_management.allocations.actions.delete_allocation" />
                  }
                >
                  <StyledButton
                    type="text"
                    danger
                    icon={
                      isDeleting ? <Loading size="small" /> : <TrashCan16 />
                    }
                    disabled={isDeleting}
                    onClick={() => {
                      setIsDeleting(true);
                      onDelete()
                        .then(() => {
                          onCancel();
                        })
                        .finally(() => {
                          setIsDeleting(false);
                        });
                    }}
                  />
                </Tooltip>
                {showSplitMode && (
                  <Tooltip
                    title={
                      <FormattedMessage id="resource_management.allocations.actions.split_time" />
                    }
                  >
                    <StyledButton
                      type="text"
                      icon={<Cut16 />}
                      onClick={() => {
                        onEnableSplitMode(allocationDetail.eventId);
                        onCancel();
                      }}
                    />
                  </Tooltip>
                )}
              </>
            ) : null}
            <Button onClick={onCancel}>
              <FormattedMessage id="cancel" />
            </Button>

            <Button
              type="primary"
              onClick={onSave}
              loading={isSubmitting}
              disabled={isSubmitting || !allowSubmit}
            >
              {/** Try showing loader on the right */}
              <FormattedMessage id="done" />
            </Button>
          </StyledButtonGroup>
        </DrawerContent.Footer>
      </Content>
    </CustomLayoutDrawer>
  );
};
